import React from 'react';
import { useNavigate } from 'react-router-dom';

/**
 * PageNotFound Component
 * Displays a professional "Page Not Found" page matching the dark XR theme.
 *
 * @param {Object} props - Component props
 * @param {string} props.bg - Background color for the page
 */
const PageNotFound = ({ bg = '#1a1a1a' }) => {
  const user_data = JSON.parse(localStorage.getItem('user'));
  const navigate = useNavigate();

  return (
    <div
      className="flex flex-col items-center justify-center min-h-screen w-full px-4 relative overflow-hidden"
      style={{ backgroundColor: bg }}
    >
      {/* Background Grid Pattern */}
      <div className="absolute inset-0 opacity-10">
        <div
          className="absolute inset-0"
          style={{
            backgroundImage: `
            linear-gradient(rgba(255,255,255,0.1) 1px, transparent 1px),
            linear-gradient(90deg, rgba(255,255,255,0.1) 1px, transparent 1px)
          `,
            backgroundSize: '50px 50px',
          }}
        ></div>
      </div>

      {/* Glowing 404 Text */}
      <div className="text-center mb-12 relative">
        <div className="relative">
          <h1 className="text-9xl font-bold text-transparent bg-clip-text bg-gradient-to-r from-red-500 to-red-600 mb-4 select-none filter drop-shadow-2xl">
            404
          </h1>
          {/* Glow effect */}
          <div className="absolute inset-0 text-9xl font-bold text-red-500 opacity-20 blur-2xl select-none">
            404
          </div>
        </div>

        {/* Red accent line similar to your "Schedule a Demo" button */}
        <div className="w-32 h-1 bg-gradient-to-r from-red-500 to-red-600 mx-auto mb-8 rounded-full shadow-lg shadow-red-500/50"></div>
      </div>

      {/* Main Content */}
      <div className="text-center max-w-2xl mx-auto mb-12 relative z-10">
        <h2 className="text-4xl font-bold text-white mb-6">Page Not Found</h2>
        <p className="text-gray-300 text-xl leading-relaxed mb-4">
          The page you're looking for doesn't exist in our XR universe.
        </p>
        <p className="text-gray-400 text-lg">
          It might have been moved to another dimension, deleted, or the URL
          might be incorrect.
        </p>
      </div>

      {/* Navigation Buttons */}
      <div className="flex flex-col sm:flex-row gap-6 items-center relative z-10">
        <button
          onClick={() => {
            if (user_data) {
              navigate('/dashboard');
            } else {
              window.location.href = process.env.REACT_APP_SPATIAL_WEB_URL;
            }
          }}
          className="bg-gradient-to-r from-red-500 to-red-600 text-white px-8 py-4 rounded-lg font-semibold text-lg shadow-2xl shadow-red-500/30 hover:shadow-red-500/50 hover:from-red-600 hover:to-red-700 transform hover:-translate-y-1 transition-all duration-300 ease-in-out border border-red-400/20"
        >
          {user_data ? '← Return to Dashboard' : '← Back to Login'}
        </button>

        <button
          onClick={() => navigate(-1)}
          className="bg-gray-800/50 text-gray-200 px-8 py-4 rounded-lg font-semibold text-lg border border-gray-600/50 backdrop-blur-sm hover:bg-gray-700/50 hover:border-gray-500/50 hover:shadow-xl transform hover:-translate-y-1 transition-all duration-300 ease-in-out"
        >
          Go Back
        </button>
      </div>

      {/* Floating VR/XR Elements */}
      <div className="absolute top-20 left-20 w-40 h-40 bg-gradient-to-br from-red-500/10 to-transparent rounded-full blur-xl animate-pulse"></div>
      <div className="absolute bottom-32 right-16 w-32 h-32 bg-gradient-to-br from-blue-500/10 to-transparent rounded-full blur-xl animate-pulse delay-1000"></div>
      <div className="absolute top-1/3 right-20 w-24 h-24 bg-gradient-to-br from-purple-500/10 to-transparent rounded-full blur-xl animate-pulse delay-500"></div>
      <div className="absolute bottom-20 left-16 w-28 h-28 bg-gradient-to-br from-green-500/10 to-transparent rounded-full blur-xl animate-pulse delay-700"></div>

      {/* VR Headset Icon */}
      <div className="absolute top-1/4 left-1/2 transform -translate-x-1/2 -translate-y-1/2 opacity-5">
        <div className="w-64 h-32 border-4 border-gray-600 rounded-3xl relative">
          <div className="absolute inset-4 bg-gray-700 rounded-2xl"></div>
          <div className="absolute -left-6 top-1/2 transform -translate-y-1/2 w-12 h-8 bg-gray-600 rounded-l-full"></div>
          <div className="absolute -right-6 top-1/2 transform -translate-y-1/2 w-12 h-8 bg-gray-600 rounded-r-full"></div>
        </div>
      </div>

      {/* Footer Text */}
      <div className="absolute bottom-8 text-center">
        <p className="text-gray-500 text-sm">
          Lost in the metaverse? Our support team can guide you back.
        </p>
      </div>

      {/* Subtle animated particles */}
      <div className="absolute inset-0 overflow-hidden pointer-events-none">
        <div
          className="absolute w-1 h-1 bg-red-400 rounded-full animate-ping"
          style={{ top: '20%', left: '15%', animationDelay: '0s' }}
        ></div>
        <div
          className="absolute w-1 h-1 bg-blue-400 rounded-full animate-ping"
          style={{ top: '60%', right: '20%', animationDelay: '2s' }}
        ></div>
        <div
          className="absolute w-1 h-1 bg-purple-400 rounded-full animate-ping"
          style={{ top: '80%', left: '25%', animationDelay: '4s' }}
        ></div>
        <div
          className="absolute w-1 h-1 bg-green-400 rounded-full animate-ping"
          style={{ top: '30%', right: '30%', animationDelay: '6s' }}
        ></div>
      </div>
    </div>
  );
};

export default PageNotFound;
